import { DniImageType } from "../../types";
import { validDniTypes } from "../../utils";
import { InputFile } from "@allaria-tech/matter/InputFile";
import { Typography } from "@allaria-tech/matter/Typography";
import { Icon } from "@allaria-tech/matter/Icon";
import { heicToJpeg, toTestId } from "../../app/utils";

import dniFrontExample from "../../assets/images/dniFrontExample.png";
import dniBackExample from "../../assets/images/dniBackExample.png";

import styles from "./UploadDniCard.module.scss";

const DNI_MAX_FILE_SIZE = 5; // Tamaño máximo en MB

const TIPS = [
  "Buena iluminación",
  "Todos los datos deben ser legibles",
  "El DNI completo, sin cortes",
];

const sideConfig: Record<
  DniImageType,
  {
    label: string;
    cryptoLabel: string;
    title: string;
    sideWord: string;
    image: string;
    imageAlt: string;
  }
> = {
  frontDNI: {
    label: "Foto del frente",
    cryptoLabel: "Frente",
    title: "Cargá fotos del frente de tu DNI",
    sideWord: "frente",
    image: dniFrontExample,
    imageAlt: "Ejemplo del frente del DNI",
  },
  backDNI: {
    label: "Foto del dorso",
    cryptoLabel: "Dorso",
    title: "Cargá fotos del dorso de tu DNI",
    sideWord: "dorso",
    image: dniBackExample,
    imageAlt: "Ejemplo del dorso del DNI",
  },
};

interface UploadDniFormProps {
  side: DniImageType;
  onFileDropError?: () => void;
  isMobile: boolean;
  isExternalLink: boolean;
  isCrypto?: boolean;
  isLoading?: boolean;
  selectedFile?: File | null;
  setSideFile: (
    side: DniImageType,
    value: { name: string; file: File } | null,
  ) => void;
  testId?: string;
}

const UploadDniForm = ({
  side,
  onFileDropError,
  isExternalLink,
  isCrypto,
  isLoading,
  isMobile,
  selectedFile,
  setSideFile,
  testId = "upload-dni-form",
}: UploadDniFormProps) => {
  const {
    container,
    externalLink,
    mobileInput,
    formFields,
    tipsRow,
    tipsImage,
    tipsText,
    tipItem,
    tipsCard,
    tipsCardImage,
    tipsCardTitle,
    tipsCardText,
    tipsList,
    cryptoForm,
  } = styles;

  const { label, cryptoLabel, title, sideWord, image, imageAlt } =
    sideConfig[side];

  const textColor = isCrypto ? "var(--typography-body-color-base)" : undefined;

  // The phone and the QR link both get the stacked card, where the tips read as
  // a plain list instead of the icon row the desktop step has room for
  const showTipsCard = isMobile || isExternalLink;

  const previewFile = selectedFile instanceof File ? selectedFile : null;

  const handleOnFileUpload = async (file: File) => {
    try {
      const convertedFile = await heicToJpeg(file);
      setSideFile(side, {
        name: convertedFile.name,
        file: convertedFile,
      });
    } catch (error) {
      onFileDropError?.();
      throw error;
    }
  };

  return (
    <form
      className={`${container} ${isExternalLink ? externalLink : ""} ${isCrypto ? cryptoForm : ""}`}
      data-testid={testId}
    >
      {isCrypto && (
        <Typography type="BODY" size="M" color={textColor}>
          {title}.
        </Typography>
      )}
      {showTipsCard ? (
        <div className={tipsCard} data-testid={`${testId}-tips`}>
          <img className={tipsCardImage} src={image} alt={imageAlt} />
          <Typography type="HEADING" size="XS" className={tipsCardTitle}>
            {title}
          </Typography>
          <div className={tipsCardText}>
            <Typography type="DETAIL" size="L">
              Asegurate de que la foto del {sideWord} sea clara:
            </Typography>
            <ul className={tipsList}>
              {TIPS.map((tip) => (
                <li key={tip}>
                  <Typography type="DETAIL" size="L">
                    {tip}
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <div className={formFields} data-testid={`${testId}-tips`}>
          <Typography type="BODY" size={isCrypto ? "M" : "L"} color={textColor}>
            Asegurate de que la foto del {sideWord} sea clara:
          </Typography>
          <div className={tipsRow}>
            <img className={tipsImage} src={image} alt={imageAlt} />
            <div className={tipsText}>
              <div className={tipItem}>
                <Icon
                  name="credit-card"
                  size="md"
                  color="var(--icon-emphasis, #2b58a1)"
                />
                <Typography type="BODY" size="M" color={textColor}>
                  DNI completo, sin cortes.
                </Typography>
              </div>
              <div className={tipItem}>
                <Icon
                  name="sunset"
                  size="md"
                  color="var(--icon-emphasis, #2b58a1)"
                />
                <Typography type="BODY" size="M" color={textColor}>
                  Buena iluminación.
                </Typography>
              </div>
              <div className={tipItem}>
                <Icon
                  name="tasks"
                  size="md"
                  color="var(--icon-emphasis, #2b58a1)"
                />
                <Typography type="BODY" size="M" color={textColor}>
                  Datos legibles.
                </Typography>
              </div>
            </div>
          </div>
        </div>
      )}

      <InputFile
        name={side}
        disabledButtons={isLoading}
        maxSize={DNI_MAX_FILE_SIZE}
        validTypes={validDniTypes}
        text={
          // There is nothing to drag from on a phone.
          isCrypto && isMobile ? (
            <p>
              <span>Seleccioná un archivo</span> de tu dispositivo
            </p>
          ) : (
            <p>
              Arrastrá o <span>seleccioná un archivo</span>
              {isCrypto && " de tu dispositivo"}
            </p>
          )
        }
        className={`${isMobile ? mobileInput : ""}`}
        label={isCrypto ? cryptoLabel : label}
        infoText={`Cargá un archivo JPG, PNG, HEIC, HEIF o PDF de hasta ${DNI_MAX_FILE_SIZE}mb.`}
        infoTextPosition="top"
        value={previewFile}
        onFileUpload={async (file) => {
          await handleOnFileUpload(file);
        }}
        onFileRemove={() => setSideFile(side, null)}
        onFileDropError={onFileDropError}
        testId={toTestId(label)}
      />
    </form>
  );
};

export default UploadDniForm;
